<script setup lang="ts">
import {
    ArrowLeft,
    ArrowLeftRight,
    ArrowUpRight,
    Building2,
    ClipboardList,
    Expand,
    Layers,
    LocateFixed,
    Minus,
    Network,
    Plus,
    ShieldCheck,
    Shrink,
} from '@lucide/vue'
import { type Node, type NodeMouseEvent, useVueFlow } from '@vue-flow/core'
import { ZhSectionHeading, ZhSystemLabel, ZhTooltip } from '@zerohuman/ui'
import { computed, type HTMLAttributes, nextTick, ref, useId, watch } from 'vue'
import EnterpriseAvatar from '@/components/EnterpriseAvatar.vue'
import FlowCanvas from '@/components/FlowCanvas.vue'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { buildOrgGraph, type OrgGraphNode, type OrgNodeKind, type OrgTreeNode, orgDepth } from '@/lib/orgGraph'

const props = defineProps<{ tree: OrgTreeNode; centeredId?: string }>()
/** `switch` names an enterprise the person holds but is not signed into; the view offers the switch. */
const emit = defineEmits<{ open: [route: string]; 'update:centeredId': [id: string]; switch: [enterpriseId: string] }>()
const KIND_LABELS: Record<OrgNodeKind, string> = {
    portfolio: 'Enterprises',
    enterprise: 'Enterprise',
    team: 'Team',
    member: 'Member',
    role: 'Role',
    task: 'Task',
}
const CHILD_LABELS: Record<OrgNodeKind, [string, string]> = {
    portfolio: ['enterprise', 'enterprises'],
    enterprise: ['team', 'teams'],
    team: ['member', 'members'],
    member: ['role', 'roles'],
    role: ['task', 'tasks'],
    task: ['task', 'tasks'],
}
const KIND_ICONS = {
    portfolio: Layers,
    enterprise: Building2,
    team: Network,
    member: ShieldCheck,
    role: ShieldCheck,
    task: ClipboardList,
}
const id = useId()
const { fitView, zoomIn, zoomOut, setCenter, viewport, dimensions, onNodesInitialized } = useVueFlow(id)
const activeId = ref(props.centeredId ?? props.tree.id)
const expanded = ref(false)
// The circles are a pan/zoom canvas. On a phone the same children read as a list, where a tap
// drills into a circle or opens a task exactly as a tap on a circle does.
const isMobile = useIsMobile()
const focusedId = ref('')
const finder = ref('')
const fitting = ref(false)
const contextHeading = ref<{
    focus: (options?: FocusOptions) => void
    scrollIntoView: (options?: ScrollIntoViewOptions) => void
} | null>(null)
let fitRequest = 0
let fitZoom = 1
const graph = computed(() => buildOrgGraph(props.tree, activeId.value))
const children = computed(() => graph.value.nodes.filter(node => !node.center))
const depth = computed(() => orgDepth(props.tree))
const parent = computed(() => graph.value.trail.at(-2) ?? null)
const zoomPercent = computed(() => Math.round(viewport.value.zoom * 100))
const nodes = computed<Node<OrgGraphNode>[]>(() =>
    graph.value.nodes.map(node => ({
        id: node.id,
        type: 'circle',
        data: node,
        position: node.position,
        draggable: false,
        connectable: false,
        zIndex: node.center ? 0 : 1,
        style: { width: `${node.size}px`, height: `${node.size}px` },
        domAttributes: { 'data-org-node': node.id, 'data-org-kind': node.kind } as HTMLAttributes,
    })),
)

/** Faces fit a team circle; more than this and the row stops being readable. */
const MAX_FACES = 8
/** Names are wider than faces, and a circle is only so tall. */
const MAX_LABELS = 4

function childLabel(node: OrgGraphNode): string {
    const [one, many] = CHILD_LABELS[node.kind]
    return `${node.childCount} ${node.childCount === 1 ? one : many}`
}
/** An enterprise not signed into has no children to count; its size is its headcount, so say that. */
function sizeLabel(node: OrgGraphNode): string {
    if (node.switchTo) {
        const people = node.weight ?? 0
        return `${people} ${people === 1 ? 'member' : 'members'}`
    }
    return childLabel(node)
}
function actionLabel(node: OrgGraphNode): string {
    if (node.switchTo) return 'Switch'
    return node.leaf ? 'Open task' : 'Explore'
}
/** A team shows who is in it; a member shows their roles by name, a role its tasks by name. */
function showsFaces(node: OrgGraphNode): boolean {
    return node.kind === 'team' && node.holds.length > 0
}
function showsNames(node: OrgGraphNode): boolean {
    return (node.kind === 'member' || node.kind === 'role') && !node.leaf
}
function faces(node: OrgGraphNode) {
    return node.holds.slice(0, MAX_FACES)
}
function names(node: OrgGraphNode) {
    return node.holds.slice(0, MAX_LABELS)
}
function overflow(node: OrgGraphNode, shown: number): number {
    return Math.max(0, node.holds.length - shown)
}
function emptyLabel(node: OrgGraphNode): string {
    return `No ${CHILD_LABELS[node.kind][1]} yet`
}
function nodeLabel(node: OrgGraphNode): string {
    if (node.switchTo) return `Switch to ${node.name}`
    return `${node.leaf ? 'Open task' : `Explore ${KIND_LABELS[node.kind].toLowerCase()}`} ${node.name}`
}
async function fitCanvas(): Promise<void> {
    const request = ++fitRequest
    fitting.value = true
    await nextTick()
    try {
        if (request !== fitRequest || !dimensions.value.width || !dimensions.value.height) return
        // An interrupted Vue Flow animation never resolves its promise. Fit atomically so
        // a quick zoom or resize cannot leave semantic navigation waiting forever.
        await fitView({ padding: 0.12, maxZoom: 1, duration: 0 })
        await nextTick()
        if (request === fitRequest && Number.isFinite(viewport.value.zoom) && viewport.value.zoom > 0) {
            fitZoom = viewport.value.zoom
        }
    } finally {
        if (request === fitRequest) fitting.value = false
    }
}
function center(nodeId: string): void {
    if (nodeId === graph.value.center.id) return
    fitting.value = true
    activeId.value = nodeId
    focusedId.value = ''
    emit('update:centeredId', nodeId)
    // The activated circle is removed at the next level. Keep keyboard focus in
    // the map so Tab continues from the new context and Escape still collapses it.
    void nextTick(() => contextHeading.value?.focus({ preventScroll: true }))
}
function activate(node: OrgGraphNode | undefined): void {
    if (!node || node.center) return
    if (node.switchTo) emit('switch', node.switchTo)
    else if (node.leaf) emit('open', node.route)
    else center(node.id)
}
function goUp(): void {
    if (parent.value) center(parent.value.id)
}
function onNodeClick({ node }: NodeMouseEvent): void {
    activate(node.data as OrgGraphNode | undefined)
}
function focusChild(): void {
    const node = children.value.find(child => child.id === finder.value)
    if (!node) return
    focusedId.value = node.id
    // Finding a circle brings its label to a readable scale; it does not navigate away.
    const request = ++fitRequest
    fitting.value = true
    void Promise.resolve(
        setCenter(node.position.x + node.size / 2, node.position.y + node.size / 2, {
            zoom: Math.min(1.2, Math.max(fitZoom, 0.85)),
            duration: 0,
        }),
    ).finally(() => {
        if (request === fitRequest) fitting.value = false
    })
    finder.value = ''
}
function drillTarget(): OrgGraphNode | undefined {
    const candidates = children.value.filter(node => !node.leaf)
    const focused = candidates.find(node => node.id === focusedId.value)
    if (focused) return focused
    const { x, y, zoom } = viewport.value
    const middle = { x: (dimensions.value.width / 2 - x) / zoom, y: (dimensions.value.height / 2 - y) / zoom }
    return candidates
        .map(node => ({
            node,
            distance: Math.hypot(
                node.position.x + node.size / 2 - middle.x,
                node.position.y + node.size / 2 - middle.y,
            ),
        }))
        .sort((a, b) => a.distance - b.distance)[0]?.node
}
watch(
    () => viewport.value.zoom,
    zoom => {
        if (fitting.value) return
        // Thresholds follow the fitted scale, so semantic zoom also works on a small screen or a large team.
        if (zoom >= fitZoom * 1.8) {
            const target = drillTarget()
            if (target) center(target.id)
        } else if (zoom <= fitZoom * 0.6 && parent.value) goUp()
    },
)
onNodesInitialized(() => {
    void fitCanvas()
})
watch(
    () => graph.value.nodes.map(node => `${node.id}:${node.size}:${node.position.x}:${node.position.y}`).join('|'),
    () => {
        void fitCanvas()
    },
)
watch(
    () => [dimensions.value.width, dimensions.value.height],
    () => {
        void fitCanvas()
    },
)
watch(
    () => [props.tree.id, props.centeredId],
    () => {
        activeId.value = props.centeredId ?? props.tree.id
    },
)
</script>

<template>
    <section class="org-map" :class="{ 'org-map-expanded': expanded }" :style="{ '--org-map-zoom': viewport.zoom }" aria-label="Org map" @keydown.esc="expanded = false">
        <header class="org-map-toolbar">
            <nav class="org-map-trail" aria-label="Map trail">
                <button v-if="parent" type="button" class="org-map-icon-button" :aria-label="`Back to ${parent.name}`" @click="goUp"><ArrowLeft :size="16" /></button>
                <template v-for="(step, index) in graph.trail" :key="step.id">
                    <span v-if="index" class="org-map-trail-sep" aria-hidden="true">/</span>
                    <button type="button" class="org-map-trail-step" :aria-current="index === graph.trail.length - 1 ? 'location' : undefined" @click="center(step.id)">{{ step.name }}</button>
                </template>
            </nav>
            <div class="org-map-actions">
                <select v-if="children.length && !isMobile" v-model="finder" aria-label="Find a circle in this view" @change="focusChild">
                    <option value="">Find a circle…</option>
                    <option v-for="node in children" :key="node.id" :value="node.id">{{ node.name }}</option>
                </select>
                <ZhTooltip :text="expanded ? 'Collapse map (Esc)' : 'Expand map'"><button type="button" class="org-map-icon-button" :aria-label="expanded ? 'Collapse map' : 'Expand map'" @click="expanded = !expanded"><Shrink v-if="expanded" :size="16" /><Expand v-else :size="16" /></button></ZhTooltip>
            </div>
        </header>
        <div class="org-map-context">
            <div><ZhSystemLabel as="span" tone="system">{{ KIND_LABELS[graph.center.kind] }}</ZhSystemLabel><ZhSectionHeading ref="contextHeading" tabindex="-1">{{ graph.center.name }}</ZhSectionHeading><p>{{ childLabel(graph.center) }} in this view</p></div>
            <button type="button" class="org-map-open" @click="emit('open', graph.center.route)">{{ graph.center.kind === 'portfolio' ? 'Open the enterprise picker' : `Open ${KIND_LABELS[graph.center.kind].toLowerCase()} page` }} <ArrowUpRight :size="14" /></button>
        </div>
        <div class="org-map-canvas">
            <ul v-if="isMobile" class="org-map-list" aria-label="Org circles">
                <li v-for="node in children" :key="node.id">
                    <button type="button" class="org-map-list-item" :data-org-node="node.id" :data-org-kind="node.kind" :data-org-leaf="node.leaf ? 'true' : undefined" :data-org-switch="node.switchTo" :aria-label="nodeLabel(node)" @click="activate(node)">
                        <MemberAvatar v-if="node.kind === 'member'" :member="{ name: node.name, avatarUrl: node.avatarUrl ?? null }" class="org-map-list-avatar" fallback-class="text-sm" />
                        <EnterpriseAvatar v-else-if="node.kind === 'enterprise' && node.avatarUrl" :enterprise="{ name: node.name, avatarUrl: node.avatarUrl }" class="org-map-list-avatar" />
                        <span v-else class="org-map-list-icon"><component :is="KIND_ICONS[node.kind]" :size="18" aria-hidden="true" /></span>
                        <span class="org-map-list-text">
                            <span class="org-map-list-name">{{ node.name }}</span>
                            <span class="org-map-list-meta">{{ node.leaf && !node.switchTo ? KIND_LABELS[node.kind] : sizeLabel(node) }}</span>
                        </span>
                        <span class="org-map-list-action">{{ actionLabel(node) }} <ArrowLeftRight v-if="node.switchTo" :size="12" /><ArrowUpRight v-else :size="12" /></span>
                    </button>
                </li>
            </ul>
            <FlowCanvas v-else :id="id" :nodes="nodes" :edges="[]" aria-label="Org circles" :pan-on-scroll="true" :zoom-on-scroll="false" :min-zoom="0.02" :max-zoom="2.5" @node-click="onNodeClick">
                <template #node-circle="{ data }">
                    <div v-if="data.center" class="org-node-boundary rounded-full" aria-hidden="true"><span>{{ KIND_LABELS[data.kind as OrgNodeKind] }}</span></div>
                    <ZhTooltip v-else :text="data.name"><button type="button" class="org-node nodrag rounded-full" :class="[`org-node-${data.kind}`, { 'org-node-focused': focusedId === data.id }]" :data-org-leaf="data.leaf ? 'true' : undefined" :data-org-switch="data.switchTo" :aria-label="nodeLabel(data)" @focus="focusedId = data.id" @click.stop="activate(data)" @keydown.enter.stop.prevent="activate(data)" @keydown.space.stop.prevent="activate(data)">
                        <MemberAvatar v-if="data.kind === 'member'" :member="{ name: data.name, avatarUrl: data.avatarUrl ?? null }" class="org-node-avatar" fallback-class="text-xl" />
                        <EnterpriseAvatar v-else-if="data.kind === 'enterprise' && data.avatarUrl" :enterprise="{ name: data.name, avatarUrl: data.avatarUrl }" class="org-node-avatar" />
                        <span v-else class="org-node-icon"><component :is="KIND_ICONS[data.kind as OrgNodeKind]" :size="25" aria-hidden="true" /></span>
                        <span class="org-node-name">{{ data.name }}</span>
                        <span v-if="data.switchTo || (!data.leaf && !showsNames(data))" class="org-node-count">{{ sizeLabel(data) }}</span>
                        <span v-if="showsFaces(data)" class="org-node-faces" aria-hidden="true">
                            <MemberAvatar
                                v-for="held in faces(data)"
                                :key="held.id"
                                :member="{ name: held.name, avatarUrl: held.avatarUrl ?? null }"
                                class="org-node-face rounded-full"
                                fallback-class="text-xs"
                            />
                            <span v-if="overflow(data, faces(data).length)" class="org-node-more">+{{ overflow(data, faces(data).length) }}</span>
                        </span>
                        <span v-else-if="showsNames(data)" class="org-node-labels">
                            <span v-for="held in names(data)" :key="held.id" class="org-node-label">{{ held.name }}</span>
                            <span v-if="overflow(data, names(data).length)" class="org-node-label org-node-more">+{{ overflow(data, names(data).length) }} more</span>
                            <span v-if="!data.holds.length" class="org-node-count">{{ emptyLabel(data) }}</span>
                        </span>
                        <span class="org-node-action">{{ actionLabel(data) }} <ArrowLeftRight v-if="data.switchTo" :size="12" /><ArrowUpRight v-else :size="12" /></span>
                    </button></ZhTooltip>
                </template>
            </FlowCanvas>
            <div v-if="!children.length" class="org-map-empty" role="status"><p>No {{ CHILD_LABELS[graph.center.kind][1] }} here yet</p><span>Open the {{ KIND_LABELS[graph.center.kind].toLowerCase() }} page to see its details.</span></div>
        </div>
        <footer class="org-map-footer">
            <div v-if="!isMobile" class="org-map-controls" aria-label="Map navigation">
                <ZhTooltip text="Zoom out"><button type="button" aria-label="Zoom out" @click="zoomOut({ duration: 180 })"><Minus :size="16" /></button></ZhTooltip>
                <output aria-label="Zoom level">{{ zoomPercent }}%</output>
                <ZhTooltip text="Zoom in"><button type="button" aria-label="Zoom in" @click="zoomIn({ duration: 180 })"><Plus :size="16" /></button></ZhTooltip>
                <span class="org-map-divider" />
                <ZhTooltip text="Fit the map to view"><button type="button" aria-label="Fit the map to view" @click="fitCanvas"><LocateFixed :size="16" /><span>Fit</span></button></ZhTooltip>
            </div>
            <p class="org-map-hint">
                {{ isMobile ? 'Tap a circle to explore' : 'Click a circle to explore · Drag to pan · Pinch or use + / − to zoom' }}
            </p>
            <span class="org-map-depth">Level {{ graph.trail.length }} of {{ depth }}</span>
        </footer>
    </section>
</template>

<style scoped>
.org-map { display: flex; flex-direction: column; width: 100%; height: 100%; min-height: 420px; border: 1px solid var(--border); border-radius: 0; overflow: hidden; background: var(--background); }
.org-map-expanded { position: fixed; inset: 16px; width: auto; height: auto; z-index: 60; box-shadow: 0 0 0 100vmax color-mix(in srgb, var(--void-black) 53%, transparent), 0 24px 80px color-mix(in srgb, var(--void-black) 20%, transparent); }
.org-map-toolbar, .org-map-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; padding: 10px 14px; background: var(--card); font-size: var(--text-xs); }
.org-map-toolbar { border-bottom: 1px solid var(--border); }
.org-map-trail, .org-map-actions { display: flex; align-items: center; gap: 5px; min-width: 0; }
.org-map-trail { flex: 1; flex-wrap: wrap; }
.org-map-trail-step { max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; border-radius: 0; padding: 5px 7px; color: var(--muted-foreground); cursor: pointer; }
.org-map-trail-step[aria-current] { color: var(--foreground); font-weight: 600; }
.org-map-trail-sep { color: var(--muted-foreground); }
.org-map-actions select { width: 155px; max-width: 100%; border: 1px solid var(--border); border-radius: 0; padding: 6px 8px; background: var(--background); color: var(--foreground); }
.org-map-icon-button, .org-map-controls button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border-radius: 0; height: 30px; min-width: 30px; padding: 0 7px; cursor: pointer; color: var(--foreground); }
.org-map button:hover { background-color: var(--muted); }
.org-map button:focus-visible, .org-map select:focus-visible { outline: 2px solid var(--electric-cyan); outline-offset: 2px; }
.org-map-context { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 20px; background: var(--card); }
.org-map-context h2 { font-size: var(--text-lg); font-weight: 600; line-height: 1.4; overflow-wrap: anywhere; }
.org-map-context p { font-size: var(--text-xs); color: var(--muted-foreground); }
.org-map-open { display: inline-flex; align-items: center; justify-content: center; gap: 6px; border: 1px solid var(--border); border-radius: 0; padding: 7px 10px; font-size: var(--text-xs); flex-shrink: 0; cursor: pointer; }
.org-map-canvas { position: relative; flex: 1; min-height: 0; background: color-mix(in srgb, var(--electric-cyan) 3%, var(--background)); }
.org-map-footer { border-top: 1px solid var(--border); }
.org-map-controls { display: flex; align-items: center; gap: 2px; }
.org-map-controls output { width: 44px; font-size: var(--text-xs); font-variant-numeric: tabular-nums; text-align: center; color: var(--muted-foreground); }
.org-map-divider { height: 18px; width: 1px; margin: 0 4px; background: var(--border); }
.org-map-hint, .org-map-depth { font-size: var(--text-xs); color: var(--muted-foreground); }
.org-map-list { position: absolute; inset: 0; display: flex; flex-direction: column; gap: 8px; overflow-y: auto; padding: 12px; }
.org-map .org-map-list-item { display: flex; align-items: center; gap: 10px; width: 100%; height: auto; min-height: 56px; padding: 12px; border: 1px solid color-mix(in srgb, var(--electric-cyan) 40%, var(--border)); border-radius: 0; background: var(--card); text-align: left; cursor: pointer; }
.org-map-list-icon { display: grid; place-items: center; width: 34px; height: 34px; flex: none; border-radius: 0; background: color-mix(in srgb, var(--electric-cyan) 7%, transparent); color: var(--electric-cyan); }
.org-map-list-avatar { width: 34px; height: 34px; flex: none; }
.org-map-list-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.org-map-list-name { font-size: var(--text-sm); font-weight: 600; overflow-wrap: anywhere; }
.org-map-list-meta, .org-map-list-action { font-size: var(--text-xs); color: var(--muted-foreground); }
.org-map-list-action { display: inline-flex; align-items: center; gap: 3px; flex: none; }
.org-node-boundary { width: 100%; height: 100%; border: 2px dashed color-mix(in srgb, var(--electric-cyan) 45%, var(--border)); background: color-mix(in srgb, var(--electric-cyan) 5%, var(--background)); text-align: center; padding-top: 28px; pointer-events: none; }
.org-node-boundary span { font-size: var(--text-base); letter-spacing: var(--tracking-system); text-transform: uppercase; color: var(--muted-foreground); }
.org-node { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 100%; padding: 18px; text-align: center; border: 2px solid color-mix(in srgb, var(--electric-cyan) 50%, var(--border)); background: var(--card); color: var(--foreground); cursor: pointer; box-shadow: 0 8px 24px color-mix(in srgb, var(--electric-cyan) 6%, transparent), 0 2px 4px color-mix(in srgb, var(--void-black) 2%, transparent); overflow: hidden; transition: box-shadow .15s, border-color .15s; }
.org-map .org-node:hover { border-color: var(--electric-cyan); background: var(--card); box-shadow: 0 0 0 6px color-mix(in srgb, var(--electric-cyan) 9%, transparent), 0 10px 25px color-mix(in srgb, var(--electric-cyan) 8%, transparent); }
.org-node-icon { display: grid; place-items: center; width: 46px; height: 46px; flex: none; border-radius: 0; background: color-mix(in srgb, var(--electric-cyan) 7%, transparent); color: var(--electric-cyan); }
.org-node-avatar { width: 48px; height: 48px; flex: none; }
.org-node-name { max-width: 100%; font-size: clamp(var(--text-lg), calc(var(--text-sm) / var(--org-map-zoom)), var(--text-3xl)); font-weight: 600; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; overflow-wrap: normal; }
.org-node-count { font-size: clamp(var(--text-sm), calc(var(--text-xs) / var(--org-map-zoom)), var(--text-2xl)); color: var(--muted-foreground); }
/* The whole circle is the target: a face must not pop its own hover card or swallow the click. */
.org-node-faces, .org-node-labels { pointer-events: none; }
.org-node-faces { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 4px; max-width: 78%; }
.org-node-face { width: clamp(22px, calc(20px / var(--org-map-zoom)), 34px); height: clamp(22px, calc(20px / var(--org-map-zoom)), 34px); border: 1px solid var(--border); overflow: hidden; }
.org-node-labels { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 4px; max-width: 86%; }
.org-node-label { max-width: 100%; border: 1px solid color-mix(in srgb, var(--electric-cyan) 35%, var(--border)); background: color-mix(in srgb, var(--electric-cyan) 6%, transparent); padding: 1px 6px; font-size: clamp(var(--text-xs), calc(var(--text-xs) / var(--org-map-zoom)), var(--text-xl)); line-height: 1.5; color: var(--foreground); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.org-node-more { font-size: clamp(var(--text-xs), calc(var(--text-xs) / var(--org-map-zoom)), var(--text-xl)); color: var(--muted-foreground); }
.org-node-action { display: inline-flex; align-items: center; gap: 3px; font-size: clamp(var(--text-xs), calc(var(--text-xs) / var(--org-map-zoom)), var(--text-xl)); color: var(--electric-cyan); }
.org-node-task { border-color: var(--border); }
.org-node-focused { border-color: var(--electric-cyan); box-shadow: 0 0 0 5px color-mix(in srgb, var(--electric-cyan) 15%, transparent); }
.org-map-empty { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(80%, 360px); padding: 20px; text-align: center; pointer-events: none; }
.org-map-empty p { font-size: var(--text-base); font-weight: 500; }
.org-map-empty span { font-size: var(--text-sm); color: var(--muted-foreground); }
.org-map :deep(.vue-flow__node) { border: 0; padding: 0; background: none; box-shadow: none; }
@media (max-width: 760px) {
    .org-map-toolbar, .org-map-footer { padding: 8px; }
    .org-map-actions select { width: 130px; }
    .org-map-context { padding: 12px; align-items: flex-start; }
    .org-map-context h2 { font-size: var(--text-base); }
    .org-map-open { font-size: var(--text-xs); padding: 6px; }
    .org-map-trail-step { max-width: 125px; }
    .org-map-hint { display: none; }
    .org-map-expanded { inset: 6px; }
}
</style>
